import { getBorder } from 'constants/Style';
import { ComponentPropsWithRef, FC, forwardRef } from 'react';
import { Button, Typography } from 'melp-design/components';
import { FormControl, FormHelperText, Stack } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import ReactQuill from 'react-quill';
import RichTextEditor from 'components/common/RichTextEditor/RichTextEditor';
import { ContentLanguage } from 'types/general';
import { useTranslation } from 'react-i18next';
import { Colors, SystemColors } from 'melp-design/style';
import {
  toPropsValue,
  toQuillValue,
} from 'components/common/RichTextEditor/converters';
import { useTranslateText } from 'store/ai-translate';
import { quillModules } from 'components/common/RichTextEditor/quill-setup';
import { formatBytes } from 'utils/format';
import { AutoAwesomeOutlined } from '@mui/icons-material';

const DEFAULT_SIZE_LIMIT_IN_BYTES = 1024 * 1024 * 8;

const calculateBytes = (html: string = '') => {
  const encoder = new TextEncoder();
  const encodedBytes = encoder.encode(html);
  return encodedBytes.length;
};

const isQuillEmpty = (quill: ReactQuill.UnprivilegedEditor) => {
  if ((quill.getContents()['ops'] || []).length !== 1) {
    return false;
  }
  return quill.getText().trim().length === 0;
};

interface StyleParams {
  minHeight: string | number;
  maxHeight: string | number;
}

const useStyles = makeStyles((theme) => ({
  richTextEditor: ({ minHeight, maxHeight }: StyleParams) => ({
    color: SystemColors.grey[45],
    '& .ql-toolbar.ql-snow': {
      border: getBorder(Colors.elementsGrey),
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      background: theme.palette.common.white,
    },
    '& .ql-container.ql-snow': {
      border: getBorder(Colors.elementsGrey),
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      background: theme.palette.common.white,
      fontFamily: '"Inter", sans-serif',
    },
    '& .ql-editor': {
      minHeight,
      maxHeight,
    },
  }),
  displayMode: {
    '&.quill .ql-toolbar.ql-snow': {
      display: 'none',
    },
    '&.quill .ql-container.ql-snow': {
      border: 'none',
      background: 'none',
    },
    '&.quill .ql-editor': {
      padding: 0,
      minHeight: 'unset',
      maxHeight: 'unset',
    },
  },
  error: {
    '&.quill .ql-toolbar.ql-snow': {
      borderTopColor: Colors.red,
      borderLeftColor: Colors.red,
      borderRightColor: Colors.red,
    },
    '&.quill .ql-container.ql-snow': {
      borderColor: Colors.red,
    },
  },
}));

interface Props
  extends Omit<ComponentPropsWithRef<typeof RichTextEditor>, 'error'> {
  label?: string;
  error?: string;
  info?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  /**
   * Indicates whether "display mode" is active. It removes toolbar and editing
   * capabilities. Only formatted text is displayed.
   */
  displayMode?: boolean;
  disabled?: boolean;
  minHeight?: string | number;
  maxHeight?: string | number;
  ai?: {
    text: string;
    inputLanguage: Uppercase<ContentLanguage>;
    outputLanguage: Uppercase<ContentLanguage>;
  };
}

export const RichTextInput: FC<Props> = forwardRef<ReactQuill, Props>(
  (
    {
      label,
      className,
      displayMode = false,
      disabled = false,
      onChange,
      value,
      minHeight = 100,
      maxHeight = 300,
      error,
      info,
      placeholder,
      id,
      ai,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const classes = useStyles({ minHeight, maxHeight });
    const handleChange: ReactQuill.ReactQuillProps['onChange'] = (
      newValue,
      _delta,
      source,
      editor,
    ) => {
      // React only to changes initiated by a user.
      // See the github issue for more details:
      //   https://github.com/zenoamaro/react-quill/issues/259
      if (source === 'user') {
        if (isQuillEmpty(editor)) {
          onChange?.('');
        } else {
          onChange?.(toPropsValue(newValue));
        }
      }
    };

    const { mutate: translateText, isLoading: isTranslating } =
      useTranslateText();

    const isDisabled = isTranslating || disabled;
    const currentSize = calculateBytes(value);
    const exceedingLimit = currentSize > DEFAULT_SIZE_LIMIT_IN_BYTES;

    return (
      <FormControl error={!!error} fullWidth>
        {label ? (
          <Typography
            variant="p1"
            color="textSecondary"
            mb="10px"
            component="label"
          >
            {label}
          </Typography>
        ) : null}

        <Stack>
          <ReactQuill
            ref={forwardedRef}
            id={id}
            placeholder={placeholder}
            theme="snow"
            modules={quillModules}
            className={clsx(
              classes.richTextEditor,
              displayMode && classes.displayMode,
              !!error && classes.error,
              className,
            )}
            readOnly={isDisabled || displayMode}
            value={toQuillValue(value)}
            onChange={handleChange}
          />
          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
            py={1}
          >
            {displayMode ? null : (
              <Typography
                variant="p2"
                color={exceedingLimit ? 'error' : 'textSecondary'}
                mt="5px"
                align="right"
              >
                {[currentSize, DEFAULT_SIZE_LIMIT_IN_BYTES]
                  .map(formatBytes)
                  .join(' / ')}
              </Typography>
            )}
            {ai && ai.inputLanguage !== ai.outputLanguage && onChange ? (
              <Button
                label={t('ai.actions.translate')}
                size="sm"
                variant="secondary"
                icon={AutoAwesomeOutlined}
                disabled={isDisabled}
                onClick={() =>
                  translateText(ai, {
                    onSuccess: ({ data }) => onChange(data.translatedText),
                  })
                }
              />
            ) : null}
          </Stack>
        </Stack>

        {error ? <FormHelperText error>{error}</FormHelperText> : null}
        {info ? <FormHelperText error={false}>{info}</FormHelperText> : null}
      </FormControl>
    );
  },
);

RichTextInput.displayName = 'RichTextInput';
